import { useState, type FormEvent } from "react";

import type { Lesson, Subject, Syllabus, Topic } from "../../services/curriculumService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { DIFFICULTIES, LESSON_TYPES } from "./syllabusLogic";

/**
 * Create and edit forms for a syllabus, its topics and their lesson plans.
 * Long plan text is split into labelled parts so each can be read on its own.
 */

const text = (data: FormData, name: string) => String(data.get(name) ?? "").trim();

function LongField({ name, label, value, hint, rows = 3 }: { name: string; label: string; value?: string | null; hint?: string; rows?: number }) {
  return (
    <Field label={label} optional hint={hint}>
      <textarea name={name} rows={rows} defaultValue={value ?? ""} className="input mt-1" />
    </Field>
  );
}

export function SyllabusForm({ record, subjects, levels, busy, error, onSave, onClose }: {
  record?: Syllabus;
  subjects: Subject[];
  levels: string[];
  busy: boolean;
  error?: string;
  onSave: (data: Partial<Syllabus>) => void;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(record ? String(record.subject) : "");
  const [level, setLevel] = useState(record?.grade_name ?? "");
  const [title, setTitle] = useState(record?.title ?? "");
  const [overview, setOverview] = useState(record?.description ?? "");
  const [start, setStart] = useState(record?.start_date ?? "");
  const [end, setEnd] = useState(record?.end_date ?? "");
  const chosen = subjects.find((item) => String(item.id) === subject);
  const suggested = chosen && level ? `${chosen.name} — ${level}` : "";
  const wrongWay = Boolean(start && end && end < start);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (wrongWay) return;
    const data = new FormData(event.currentTarget);
    onSave({
      subject: Number(subject),
      ...(record && level === record.grade_name ? {} : { class_level: level }),
      title: title.trim() || suggested,
      version: text(data, "version") || "1.0",
      start_date: start,
      end_date: end || undefined,
      description: overview.trim(),
      learning_outcomes: text(data, "learning_outcomes"),
      assessment_objectives: text(data, "assessment_objectives"),
      assessment_methods: text(data, "assessment_methods"),
      grading_criteria: text(data, "grading_criteria"),
      teaching_materials: text(data, "teaching_materials"),
      recommended_books: text(data, "recommended_books"),
      digital_resources: text(data, "digital_resources"),
      notes: text(data, "notes"),
    });
  };
  return (
    <FormModal
      id="syllabus-form"
      title={record ? `Edit ${record.title}` : "New syllabus"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create syllabus"}
      note="One syllabus covers every stream of the class level."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="What it covers" divided={false} columns={2}>
        <Field label="Subject">
          <select required value={subject} onChange={(event) => setSubject(event.target.value)} className="input mt-1">
            <option value="">Choose a subject</option>
            {subjects.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.curriculum_name}</option>)}
          </select>
        </Field>
        <Field label="Class level" hint={levels.length ? "Every stream of this level follows it." : "Create the classes under Classes & streams first."}>
          <select required value={level} onChange={(event) => setLevel(event.target.value)} className="input mt-1">
            <option value="">Choose a class level</option>
            {[...new Set([...(record ? [record.grade_name] : []), ...levels])].map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </Field>
        <Field label="Title" className="sm:col-span-2" hint={!title && suggested ? `Left blank, it is called “${suggested}”.` : undefined}>
          <input value={title} onChange={(event) => setTitle(event.target.value)} className="input mt-1" placeholder={suggested || "e.g. Mathematics — P5"} />
        </Field>
        <Field label="Version" hint="Change it when the syllabus is revised.">
          <input name="version" defaultValue={record?.version ?? "1.0"} className="input mt-1" />
        </Field>
        <div />
        <Field label="In use from">
          <input required type="date" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="In use until" optional hint={wrongWay ? <span className="text-red-700">This is before it starts.</span> : "Blank while it is current."}>
          <input type="date" min={start || undefined} value={end} onChange={(event) => setEnd(event.target.value)} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="Aims" columns={1}>
        <Field as="div" label="Overview" optional>
          <AIAssistField
            rows={3}
            value={overview}
            onChange={(next) => setOverview(next)}
            placeholder="What learners study in this subject at this level"
            context={record ? { key: "curriculum.teaching_plan", params: { kind: "syllabus", syllabus: record.id } } : undefined}
            draftLabel="Draft overview"
            draftHint="Drawn from the topics once they are added."
          />
        </Field>
        <LongField name="learning_outcomes" label="Learning outcomes" value={record?.learning_outcomes} hint="What learners can do by the end, one per line." rows={4} />
      </FormSection>

      <FormSection title="Assessment" columns={1}>
        <LongField name="assessment_objectives" label="What is assessed" value={record?.assessment_objectives} />
        <LongField name="assessment_methods" label="How it is assessed" value={record?.assessment_methods} hint="e.g. weekly exercises, beginning-, mid- and end-of-term tests." />
        <LongField name="grading_criteria" label="Grading" value={record?.grading_criteria} rows={2} />
      </FormSection>

      <FormSection title="Resources" columns={1}>
        <LongField name="recommended_books" label="Recommended books" value={record?.recommended_books} />
        <LongField name="teaching_materials" label="Teaching materials" value={record?.teaching_materials} />
        <LongField name="digital_resources" label="Digital resources" value={record?.digital_resources} rows={2} />
        <LongField name="notes" label="Notes" value={record?.notes} rows={2} />
      </FormSection>
    </FormModal>
  );
}

export function TopicForm({ syllabus, record, nextOrder, busy, error, onSave, onClose }: {
  syllabus: Syllabus;
  record?: Topic;
  nextOrder: number;
  busy: boolean;
  error?: string;
  onSave: (data: Partial<Topic>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(record?.name ?? "");
  const [description, setDescription] = useState(record?.description ?? "");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({
      syllabus: syllabus.id,
      name: name.trim(),
      order: Number(data.get("order")),
      duration_weeks: Number(data.get("duration_weeks")),
      difficulty_level: text(data, "difficulty_level"),
      description: description.trim(),
      learning_objectives: text(data, "learning_objectives"),
      key_concepts: text(data, "key_concepts"),
      skills_to_develop: text(data, "skills_to_develop"),
      teaching_strategies: text(data, "teaching_strategies"),
      learning_activities: text(data, "learning_activities"),
      resources_needed: text(data, "resources_needed"),
      assessment_methods: text(data, "assessment_methods"),
      is_active: true,
    });
  };
  return (
    <FormModal
      id="topic-form"
      title={record ? `Edit ${record.name}` : `New topic · ${syllabus.title}`}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Add topic"}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Topic" divided={false} columns={2}>
        <Field label="Name" className="sm:col-span-2">
          <input required autoFocus value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Fractions" />
        </Field>
        <Field label="Order" hint="Topics are taught from the lowest number up.">
          <input required type="number" min={0} name="order" defaultValue={record ? record.order : nextOrder} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Weeks">
          <input required type="number" min={1} name="duration_weeks" defaultValue={record?.duration_weeks ?? 1} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Level of challenge">
          <select name="difficulty_level" defaultValue={record?.difficulty_level ?? "beginner"} className="input mt-1">
            {DIFFICULTIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
      </FormSection>
      <FormSection title="What it covers" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What learners cover in this topic"
            context={{ key: "curriculum.teaching_plan", params: { kind: "topic", syllabus: syllabus.id, name } }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the subject, class level and the other topics." : "Name the topic first, then draft."}
          />
        </Field>
        <LongField name="learning_objectives" label="Learning objectives" value={record?.learning_objectives} hint="One per line." rows={4} />
        <LongField name="key_concepts" label="Key concepts" value={record?.key_concepts} />
        <LongField name="skills_to_develop" label="Skills developed" value={record?.skills_to_develop} />
      </FormSection>
      <FormSection title="Teaching it" columns={1}>
        <LongField name="teaching_strategies" label="Teaching approaches" value={record?.teaching_strategies} />
        <LongField name="learning_activities" label="Learning activities" value={record?.learning_activities} />
        <LongField name="resources_needed" label="Resources needed" value={record?.resources_needed} />
        <LongField name="assessment_methods" label="How it is assessed" value={record?.assessment_methods} rows={2} />
      </FormSection>
    </FormModal>
  );
}

export function LessonForm({ syllabus, topic, record, nextOrder, busy, error, onSave, onClose }: {
  syllabus: Syllabus;
  topic: Topic;
  record?: Lesson;
  nextOrder: number;
  busy: boolean;
  error?: string;
  onSave: (data: Partial<Lesson>) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(record?.title ?? "");
  const [type, setType] = useState(record?.lesson_type ?? "theory");
  const [minutes, setMinutes] = useState(String(record?.duration_minutes ?? 40));
  const [objectives, setObjectives] = useState(record?.learning_objectives ?? "");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({
      topic: topic.id,
      title: title.trim(),
      lesson_type: type,
      duration_minutes: Number(minutes),
      order: Number(data.get("order")),
      learning_objectives: objectives.trim(),
      success_criteria: text(data, "success_criteria"),
      introduction: text(data, "introduction"),
      main_activities: text(data, "main_activities"),
      conclusion: text(data, "conclusion"),
      homework: text(data, "homework"),
      teaching_materials: text(data, "teaching_materials"),
      assessment_methods: text(data, "assessment_methods"),
      is_active: true,
    });
  };
  return (
    <FormModal
      id="lesson-form"
      title={record ? `Edit ${record.title}` : `New lesson plan · ${topic.name}`}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Add lesson plan"}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Lesson" divided={false} columns={2}>
        <Field label="Title" className="sm:col-span-2">
          <input required autoFocus value={title} onChange={(event) => setTitle(event.target.value)} className="input mt-1" placeholder="e.g. Adding fractions with the same denominator" />
        </Field>
        <Field label="Order in the topic" hint="Lessons run from the lowest number up.">
          <input required type="number" min={0} name="order" defaultValue={record ? record.order : nextOrder} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Kind of lesson">
          <select value={type} onChange={(event) => setType(event.target.value)} className="input mt-1">
            {LESSON_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Minutes">
          <input required type="number" min={10} max={240} value={minutes} onChange={(event) => setMinutes(event.target.value)} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="Aims" columns={1}>
        <Field as="div" label="Learning objectives" optional>
          <AIAssistField
            rows={4}
            value={objectives}
            onChange={(next) => setObjectives(next)}
            placeholder="Learners will be able to…"
            context={{ key: "curriculum.teaching_plan", params: { kind: "lesson", syllabus: syllabus.id, topic: topic.id, name: title, lesson_type: type, duration_minutes: minutes } }}
            draftLabel="Draft objectives"
            draftHint={title.trim() ? "Uses the lesson, its topic's objectives and the class level." : "Give the lesson a title first, then draft."}
          />
        </Field>
        <LongField name="success_criteria" label="How you will know they have learnt it" value={record?.success_criteria} />
      </FormSection>
      <FormSection title="The lesson, step by step" columns={1}>
        <LongField name="introduction" label="Introduction" value={record?.introduction} />
        <LongField name="main_activities" label="Main activities" value={record?.main_activities} rows={4} />
        <LongField name="conclusion" label="Conclusion" value={record?.conclusion} />
        <LongField name="homework" label="Homework" value={record?.homework} rows={2} />
      </FormSection>
      <FormSection title="Resources and assessment" columns={1}>
        <LongField name="teaching_materials" label="Teaching materials" value={record?.teaching_materials} rows={2} />
        <LongField name="assessment_methods" label="How it is assessed" value={record?.assessment_methods} rows={2} />
      </FormSection>
    </FormModal>
  );
}
